import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { newsService } from '../../../services/news.service.js';
import { useAuth } from '../../../context/AuthContext';
import Loader from '../../common/Loader/Loader';
import NewsCard from './NewsCard';
import Dropdown from '../../common/Dropdown/Dropdown';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import MyPublisherStats from './MyPublisherStats';
import './NewsPage.css';

/**
 * Everything the paste box has to say, drawn inside the box itself.
 *
 * Not the textarea's `placeholder`, even though that is what this replaces: a
 * placeholder takes one style for all of it, and the parts that matter here -
 * the shortcuts, and the setting Safari needs before it has a View Source at
 * all - are worth picking out from the prose around them.
 *
 * So it is an overlay sitting in the textarea's own box, with pointer events
 * off: clicks and paste land on the textarea underneath as if this were not
 * here, and it is dropped from the DOM the moment anything is pasted. It stays
 * inside the box for the same reason the placeholder did - spelled out around
 * the textarea, these lines pushed the generator section taller than the
 * preview beside it.
 */
const PageSourceGuide = () => (
  <div className="news-source-guide" id="page-source-guide">
    <p className="news-source-guide-lead">
      Paste the page source <mark>in this box</mark>
    </p>
    <ol className="news-source-guide-steps">
      <li>
        Open the article from the <strong>link above</strong>
      </li>
      <li>
        Open its page source
        <span className="news-source-guide-browsers">
          <span className="news-source-guide-browser">Chrome / Edge</span>
          <span>
            <kbd>Ctrl</kbd>+<kbd>U</kbd>
            <span className="news-source-guide-mac">Mac</span>
            <kbd>⌥</kbd>+<kbd>⌘</kbd>+<kbd>U</kbd>
          </span>

          <span className="news-source-guide-browser">Firefox</span>
          <span>
            <kbd>Ctrl</kbd>+<kbd>U</kbd>
            <span className="news-source-guide-mac">Mac</span>
            <kbd>⌘</kbd>+<kbd>U</kbd>
          </span>

          <span className="news-source-guide-browser">Safari</span>
          <span>
            <kbd>⌥</kbd>+<kbd>⌘</kbd>+<kbd>U</kbd>, only after{' '}
            <mark>Settings → Advanced → &ldquo;Show features for web developers&rdquo;</mark>
          </span>
        </span>
        <span className="news-source-guide-note">
          Or right-click an empty part of the page →{' '}
          <strong>View page source</strong> (Safari: <strong>Show Page Source</strong>)
        </span>
      </li>
      <li>
        Select all, copy, and <mark>paste it here</mark>
      </li>
    </ol>
  </div>
);

// Admins and super admins oversee everyone's work, so the landing filters never
// narrow the listing down to their own shorts
const isAdminUser = (user) => user?.role === 'admin' || user?.role === 'super_admin';

const NewsPage = () => {
  const { categories } = useOutletContext() || {};
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  // Default to the user's first assigned language permission, if any;
  // they can still freely change it afterwards. An admin lands on everything,
  // whatever languages they happen to be granted.
  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    !isAdminUser(user) && user?.lang?.length > 0 ? user.lang[0] : 'all'
  );
  const [onlyLive, setOnlyLive] = useState(false);
  // Landing filter: the search box matches the creator's name/email too, so
  // seeding it with the user's own email lands them on their own shorts.
  // Cleared automatically below when they have none yet. Admins get the whole
  // listing, so no publisher filter is seeded for them.
  const [searchQuery, setSearchQuery] = useState(() =>
    isAdminUser(user) ? '' : user?.email || ''
  );
  // Set once the publisher filter has been dropped for want of own shorts, so
  // the page can say why it is showing someone else's work
  const [publisherFallback, setPublisherFallback] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const loaderRef = useRef(null);
  const fetchInProgressRef = useRef(false);
  // Landing defaults are applied once - after that the filters belong to the user
  const landingDefaultsRef = useRef(false);
  // 'none' -> 'all-languages' -> 'publisher-dropped', see fetchNews
  const fallbackStageRef = useRef('none');

  // News generation states
  const [generateUrl, setGenerateUrl] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  // Set while the article is being fetched by the browser because the
  // publisher's CDN refuses our server
  const [generateNotice, setGenerateNotice] = useState(null);
  const [generatedNews, setGeneratedNews] = useState(null);
  const [isGeneratorCollapsed, setIsGeneratorCollapsed] = useState(true);

  // Set once a URL has defeated both the server and the browser. Carries the
  // record the failure was parked as, so the pasted rescue below continues on
  // that same row instead of opening a second one for the same URL.
  const [sourceFallback, setSourceFallback] = useState(null); // { url, newsId }
  const [pageSource, setPageSource] = useState('');
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteError, setPasteError] = useState(null);

  // News decks state
  const [newsDecks, setNewsDecks] = useState({});


  const languages = [
    { id: 'all', name: 'All' },
    { id: 'en', name: 'English' },
    { id: 'hi', name: 'Hindi' },
    { id: 'ta', name: 'Tamil' },
    { id: 'te', name: 'Telugu' },
    { id: 'kn', name: 'Kannada' },
    { id: 'gu', name: 'Gujarati' },
    { id: 'mr', name: 'Marathi' },
    { id: 'bn', name: 'Bengali' },
    { id: 'or', name: 'Odia' },
  ];

  // The publisher filter this page lands on - their own shorts, and nothing
  // for an admin, who is meant to see everybody's
  const ownPublisherFilter = isAdminUser(user) ? '' : user?.email || '';
  const isOwnPublisherView =
    Boolean(ownPublisherFilter) && searchQuery.trim() === ownPublisherFilter;

  // AuthContext can resolve after the first render, in which case the state
  // initialisers above missed the user. Apply the landing defaults once, and
  // never touch a filter the user has already set themselves.
  useEffect(() => {
    if (landingDefaultsRef.current || !user) return;
    landingDefaultsRef.current = true;

    // Nothing to seed for an admin - the unfiltered listing is their landing
    if (isAdminUser(user)) return;

    if (ownPublisherFilter) {
      setSearchQuery((prev) => prev || ownPublisherFilter);
    }
    if (user.lang?.length > 0) {
      setSelectedLanguage((prev) => (prev === 'all' ? user.lang[0] : prev));
    }
  }, [user]);

  const fetchNews = async (pageNum = 1, isLoadMore = false) => {
    // Prevent duplicate calls when a fetch is already in progress
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setPageLoading(true);
    }
    setError(null);

    try {
      const params = {
        page: pageNum,
      };

      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      if (selectedLanguage && selectedLanguage !== 'all') {
        params.language = selectedLanguage;
      }

      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (onlyLive) {
        params.isLive = 'true';
      }

      const response = await newsService.getMyNews(params);

      if (response.success) {
        let fetchedNews;
        if (isLoadMore) {
          setNews((prev) => {
            fetchedNews = [...prev, ...response.data];
            return fetchedNews;
          });
        } else {
          setNews(response.data);
          fetchedNews = response.data;

          // Nothing under the landing filter - step back rather than leave the
          // page empty. First across every language, since a user granted more
          // than one may not work in their first; only then off the publisher
          // filter, onto their granted language. Both steps run once, and only
          // while the publisher box still holds the landing default, so a
          // search the user typed themselves is never touched.
          if (response.data.length === 0 && isOwnPublisherView) {
            if (fallbackStageRef.current === 'none' && selectedLanguage !== 'all') {
              fallbackStageRef.current = 'all-languages';
              setSelectedLanguage('all');
            } else if (fallbackStageRef.current !== 'publisher-dropped') {
              fallbackStageRef.current = 'publisher-dropped';
              setPublisherFallback(true);
              setSearchQuery('');
              if (user?.lang?.length > 0) {
                setSelectedLanguage(user.lang[0]);
              }
            }
          }
        }
        // Check if there are more pages
        setHasMore(response.pagination?.totalPages > pageNum);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch shorts');
    } finally {
      fetchInProgressRef.current = false;
      setLoading(false);
      setPageLoading(false);
      setLoadingMore(false);
    }
  };

  // Debounced fetch news to avoid duplicate API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchNews(1, false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedCategory, selectedLanguage, onlyLive, searchQuery, refreshTrigger]);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !pageLoading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchNews(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, pageLoading, loadingMore, page]);

  const handleEdit = async (newsId, editData) => {
    try {
      const response = await newsService.editNewsWithHistory(newsId, editData);
      if (response.success) {
        setNews(news.map((n) => (n._id === newsId ? response.data.news : n)));
      }
    } catch (err) {
      alert('Failed to update shorts');
    }
  };

  const handleDelete = async (newsId) => {
    try {
      await newsService.deleteNews(newsId);
      setNews(news.filter((n) => n._id !== newsId));
    } catch (err) {
      alert('Failed to delete shorts');
    }
  };

  // Validate URL for button enable/disable
  const isValidUrl = () => {
    if (!generateUrl || !generateUrl.trim()) return false;
    const trimmedUrl = generateUrl.trim();
    return trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://') || trimmedUrl.includes('.');
  };

  const handleGenerateNews = async (e) => {
    e.preventDefault();

    if (!generateUrl || !generateUrl.trim()) {
      setGenerateError('Please enter a URL');
      return;
    }

    let finalUrl = generateUrl.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }

    setGenerateLoading(true);
    setGenerateError(null);
    setGenerateNotice(null);
    // Any open paste box belongs to the previous attempt
    setSourceFallback(null);
    setPageSource('');
    setPasteError(null);

    try {
      const response = await newsService.generateNews(finalUrl, undefined, {
        onClientFetchStart: () =>
          setGenerateNotice("This publisher blocks our server - reading the article from your browser instead..."),
      });

      if (response.success) {
        const newsItem = response.data.news;
        setGeneratedNews(newsItem);
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (err) {
      // A browser-side fetch failure is a plain Error with a ready message;
      // anything from the server arrives as an axios error
      let errorMessage = err.response?.data?.message || err.message || 'Failed to generate shorts';

      // Show user-friendly message for nitter instance failures
      if (errorMessage.includes('All nitter instances failed') || errorMessage.includes('HTTP 502')) {
        errorMessage = "We're unable to load this article as the source website has access restrictions.";
      }

      setGenerateError(errorMessage);

      // Nothing could fetch the page, but the editor can still open it in a tab
      // and paste what View page source shows. Offered for every fetch-stage
      // failure, whatever the reason - a copy-paste works regardless of why the
      // request was refused.
      if (err.canPasteSource) {
        setSourceFallback({ url: finalUrl, newsId: err.failedNewsId || null });
      }
    } finally {
      setGenerateLoading(false);
      setGenerateNotice(null);
    }
  };

  /**
   * Turn pasted page source into a short.
   *
   * Picks up where the failed attempt left off: the same news record is reused
   * server-side, so the short keeps that id and the fetch errors that sent us
   * down this route.
   */
  const handleGenerateFromPageSource = async (e) => {
    e.preventDefault();

    if (!sourceFallback) return;

    setPasteLoading(true);
    setPasteError(null);

    try {
      const response = await newsService.generateNewsFromPageSource(
        sourceFallback.url,
        pageSource,
        sourceFallback.newsId
      );

      if (response.success) {
        setGeneratedNews(response.data.news);
        setRefreshTrigger((prev) => prev + 1);
        // Rescued - nothing left for the box to do
        setSourceFallback(null);
        setPageSource('');
        setGenerateError(null);
        setGenerateUrl('');
      }
    } catch (err) {
      // Extraction runs in the browser before anything is sent, so a bad paste
      // arrives as a plain Error with a ready message; past that it's axios
      setPasteError(
        err.response?.data?.message || err.message || 'Could not create a short from this page source'
      );
    } finally {
      setPasteLoading(false);
    }
  };

  /**
   * Give up on this URL.
   *
   * The failure stays parked as a `failed` record - already out of the listings
   * and on the Errored News screen - so there is nothing to tell the server.
   * This just clears the form for the next URL.
   */
  const handleSkipPageSource = () => {
    setSourceFallback(null);
    setPageSource('');
    setPasteError(null);
    setGenerateError(null);
    setGenerateUrl('');
  };

  const handleDeleteGenerated = () => {
    setGeneratedNews(null);
    setGenerateUrl('');
  };

  // A card renders from the `news` prop it was handed, so going live has to be
  // written back here - otherwise the preview keeps offering Go Live for a
  // short that is already in the feed. Re-read rather than trusting the
  // response body: go-live answers with the document as it stood before the
  // publish. The list below is refetched for the same reason.
  const handleGoLiveSuccess = async (data) => {
    setRefreshTrigger((prev) => prev + 1);

    const newsId = data?.news?._id;
    if (!newsId || generatedNews?._id !== newsId) return;

    try {
      const response = await newsService.getNewsById(newsId);
      if (response?.data?.news) {
        setGeneratedNews((current) =>
          current && current._id === newsId ? response.data.news : current
        );
      }
    } catch (err) {
      console.error('Failed to refresh the generated card after go live', err);
    }
  };

  if (loading) {
    return (
      <div className="news-page">
        <div className="news-page-loading">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="news-page">
      <div className="news-page-header">
        <MyPublisherStats />
        <div className="news-filters">
          <div className="news-search-box">
            <input
              type="text"
              id="news-search"
              className="news-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              placeholder="Search by text/publisher/news id/hash id..."
            />
          </div>
          <Dropdown
            options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
            selectedValue={selectedCategory}
            onSelect={setSelectedCategory}
            type="category"
          />
          <Dropdown
            options={languages.map((lang) => ({ value: lang.id, label: lang.name }))}
            selectedValue={selectedLanguage}
            onSelect={setSelectedLanguage}
            type="language"
          />
          <label className="news-live-toggle">
            <input
              type="checkbox"
              checked={onlyLive}
              onChange={(e) => setOnlyLive(e.target.checked)}
            />
            <span className="news-live-toggle-track">
              <span className="news-live-toggle-thumb" />
            </span>
            <span className="news-live-toggle-label">Only Live</span>
          </label>
        </div>

        {/* Says why the page is showing other people's shorts after the
            publisher filter was dropped for want of the user's own */}
        {publisherFallback && !searchQuery.trim() && (
          <div className="news-filter-note">
            You have not created any shorts yet - showing{' '}
            {selectedLanguage === 'all'
              ? 'all'
              : languages.find((lang) => lang.id === selectedLanguage)?.name || selectedLanguage}{' '}
            shorts instead.
            {ownPublisherFilter && (
              <button
                type="button"
                className="news-filter-note-action"
                onClick={() => setSearchQuery(ownPublisherFilter)}
              >
                Show only mine
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="news-page-error">
          {error}
          <button onClick={() => fetchNews(1, false)} className="news-page-retry">
            Retry
          </button>
        </div>
      )}

      {/* Section 1: News Generator */}
      <div className={`news-generator-section ${isGeneratorCollapsed ? 'collapsed' : ''}`}>
        {/* Collapsible Header */}
        <div
          className="news-generator-header"
          onClick={() => setIsGeneratorCollapsed(!isGeneratorCollapsed)}
        >
          <div className="news-generator-header-content">
            <div className="news-generator-header-subtitle-wrapper">
              <span className={`news-generator-toggle-icon ${isGeneratorCollapsed ? 'collapsed' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 5L10 10L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <p className="news-generator-header-subtitle">Click here to generate Shorts</p>
              <span className={`news-generator-toggle-icon ${isGeneratorCollapsed ? 'collapsed' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 8L10 13L15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 5L10 10L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Collapsible Content */}
        <div className={`news-generator-content ${isGeneratorCollapsed ? 'hidden' : ''}`}>
          {/* Section 1a: URL Input & Button */}
          <div className="news-generator-input-panel">
            <div className="news-generation-form">
              {/* The error stays on screen while the paste box is open - it is
                  the reason the box is there */}
              {generateError && <div className="news-generation-error">{generateError}</div>}
              {generateNotice && <div className="news-generation-notice">{generateNotice}</div>}

              {/* Hidden once a URL has failed: the field and the button have
                  nothing left to offer for it, and the paste box below is the
                  only way forward. Skip brings them back. */}
              {!sourceFallback && (
                <form onSubmit={handleGenerateNews} className="news-generation-form-inner">
                  <Input
                    type="url"
                    name="url"
                    value={generateUrl}
                    onChange={(e) => {
                      setGenerateUrl(e.target.value);
                      setGenerateError(null);
                    }}
                    placeholder="https://example.com/article"
                    disabled={generateLoading}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    loading={generateLoading}
                    disabled={generateLoading || !isValidUrl()}
                    fullWidth
                  >
                    {generateLoading ? 'Generating...' : 'Generate Shorts'}
                  </Button>
                </form>
              )}
            </div>

            {/* Last resort, shown only once a URL has defeated both fetch
                routes: the editor opens the page themselves, copies View page
                source and pastes it here. The short that comes out continues on
                the same record the failed attempt was parked as. */}
            {sourceFallback && (
              <form className="news-source-fallback" onSubmit={handleGenerateFromPageSource}>
                {/* Opening the article is step one, so the URL is the link
                    rather than something to copy out by hand. The only thing
                    here that is not in the box itself, because it has to be
                    clickable. */}
                <a
                  className="news-source-fallback-link"
                  href={sourceFallback.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={sourceFallback.url}
                >
                  <span className="news-source-fallback-link-url">{sourceFallback.url}</span>
                  <ExternalLink size={14} />
                </a>

                {pasteError && <div className="news-generation-error">{pasteError}</div>}

                <div className="news-source-fallback-box">
                  <textarea
                    className="news-source-fallback-input"
                    value={pageSource}
                    onChange={(e) => {
                      setPageSource(e.target.value);
                      setPasteError(null);
                    }}
                    aria-label="Page source"
                    aria-describedby="page-source-guide"
                    spellCheck={false}
                    disabled={pasteLoading}
                  />
                  {!pageSource && <PageSourceGuide />}
                </div>

                <div className="news-source-fallback-actions">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={pasteLoading}
                    disabled={pasteLoading || !pageSource.trim()}
                  >
                    {pasteLoading ? 'Working...' : 'Short Now'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSkipPageSource}
                    disabled={pasteLoading}
                  >
                    Skip
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Vertical Divider */}
          <div className="news-vertical-divider"></div>

          {/* Section 1b: Generated Card Preview */}
          <div className="news-generator-preview-panel">
            {/* A pasted rescue runs the same AI generate, so it waits in the
                same place a URL generate does */}
            {(generateLoading || pasteLoading) && (
              <div className="news-generation-loading">
                <Loader />
              </div>
            )}

            {!generateLoading && !pasteLoading && !generatedNews && (
              <div className="news-generator-placeholder">
                <div className="news-generator-placeholder-icon">🧾</div>
                <p className="news-generator-placeholder-text">Generated news shorts will appear here</p>
              </div>
            )}

            {generatedNews && !generateLoading && !pasteLoading && (
              <div className="news-generation-preview">
                <div className="news-generation-preview-header">
                  <h4>Generated Shorts</h4>
                  <button className="news-generation-close-btn" onClick={handleDeleteGenerated}>
                    ✕
                  </button>
                </div>
                <div className="news-generation-preview-content">
                  <NewsCard
                    key={generatedNews._id}
                    news={generatedNews}
                    onEdit={handleEdit}
                    showEdit={true}
                    showAddToDeckButton={false}
                    onGoLiveSuccess={handleGoLiveSuccess}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Horizontal Divider */}
      <div className="news-section-divider"></div>

      {/* Section 2: Already Generated News Shorts */}
      <div className="news-existing-section">
        <div className="news-cards-section">
          {pageLoading && (
            <div className="news-cards-loading">
              <Loader />
            </div>
          )}
          {!pageLoading && news.length === 0 ? (
            <div className="news-page-empty">
              <div className="news-page-empty-icon">🧾</div>
              <h3>No shorts found</h3>
              <p>Generate shorts from the dashboard or try a different category.</p>
            </div>
          ) : (
            !pageLoading && (
              <div className="news-cards-grid">
                {news.map((item) => (
                  <NewsCard
                    key={item._id}
                    news={item}
                    onEdit={handleEdit}
                    showEdit={true}
                    showAddToDeckButton={false}
                    newsDecks={newsDecks[item._id] || []}
                    onGoLiveSuccess={handleGoLiveSuccess}
                  />
                ))}
                {/* Infinite scroll loader */}
                {hasMore && (
                  <div ref={loaderRef} className="infinite-scroll-loader">
                    {loadingMore && <Loader />}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
